import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DashboardLayout } from '../common/page_component/index.js';
import Loader from "../common/Loader.js";
import Alert from '../common/Alert.js';
import loanGivenService from '../../services/LoanGivenService.js';
import { Plus, Filter, ArrowLeft, Pencil, Package, MoreVertical, HandCoins, XCircle } from "lucide-react";
import Button from '../common/Button.js';
import { useTranslation } from 'react-i18next';
import { formatUsernameLocation } from '../../utils/userDisplay';


const ViewLoanGiven = () => {
  const { t } = useTranslation();
  const { loanId } = useParams();
  const [loan, setLoan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAllRepayments, setShowAllRepayments] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchLoanDetails();
  }, [loanId]);

  const fetchLoanDetails = async () => {
    try {
      const response = await loanGivenService.getById(loanId);
      setLoan(response.data);
    } catch (err) {
      Alert.error('Failed to load loan details');
      console.error(err);
      navigate('/loans-given');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    const confirmed = await Alert.confirm(
      `Approve this loan for ${loan.borrower_name}?`,
      'Approve Loan'
    );

    if (confirmed) {
      try {
        await loanGivenService.approve(loanId);
        Alert.success('Loan approved successfully!');
        fetchLoanDetails();
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to approve loan');
      }
    }
  };

  const handleReject = async () => {
    const reason = await Alert.prompt(
      'Enter rejection reason:',
      'Reject Loan'
    );

    if (reason !== null) {
      try {
        await loanGivenService.reject(loanId, reason || 'No reason provided');
        Alert.success('Loan rejected successfully!');
        fetchLoanDetails();
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to reject loan');
      }
    }
  };

  const handleActivate = async () => {
    const confirmed = await Alert.confirm(
      'Activate this loan?',
      'Activate Loan'
    );

    if (confirmed) {
      try {
        await loanGivenService.activate(loanId);
        Alert.success('Loan activated successfully!');
        fetchLoanDetails();
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to activate loan');
      }
    }
  };

  const viewAgreementDocument = () => {
    const url = loan?.agreement_url;
    if (url) {
      window.open(url, '_blank');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateShort = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      pending: 'bg-yellow-100 text-yellow-700',
      approved: 'bg-blue-100 text-blue-700',
      active: 'bg-green-100 text-green-700',
      completed: 'bg-gray-100 text-gray-700',
      rejected: 'bg-red-100 text-red-700',
      defaulted: 'bg-red-200 text-red-800',
      cancelled: 'bg-gray-200 text-gray-800'
    };
    
    return statusColors[status] || 'bg-gray-100 text-gray-700';
  };

  const displayedRepayments = showAllRepayments ? loan?.repayments : loan?.repayments?.slice(0, 5);

  if (loading) {
    return (
      <DashboardLayout>
          <div className="flex items-center justify-center py-20">
            <Loader fullScreen={false} size="default" message="Loading loan details..." />
        </div>
      </DashboardLayout>
    );
  }

  if (!loan) {
    return null;
  }

  return (
    <DashboardLayout>
                {/* Header + Actions */}
                <div className="mb-4 sm:mb-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">

                    {/* LEFT — HEADER */}
                    <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                      <HandCoins className="w-6 h-6 text-buttonBg" />
                      Loan Details
                    </h1>

                    {/* RIGHT — ACTION BUTTONS */}
                    <div className="flex flex-wrap justify-end gap-2 sm:gap-3">

                      {loan.status === "pending" && (
                        <>
                          <Button variant="primary" icon={HandCoins} onClick={handleApprove}>
                            Approve Loan
                          </Button>

                          <Button variant="primary" icon={XCircle} onClick={handleReject}>
                            Reject Loan
                          </Button>
                        </>
                      )}

                      {loan.status === "approved" && (
                        <Button variant="primary" onClick={handleActivate}>
                          Activate Loan
                        </Button>
                      )}

                      {(loan.status === "approved" || loan.status === "active") && (
                        <button
                          onClick={() => navigate(`/loans-given/repayment/${loanId}`)}
                          className="group flex items-center justify-center gap-2 px-3 sm:px-3 py-2.5 sm:py-2 text-xs sm:text-sm text-buttonTxt bg-buttonBg hover:text-buttonBg hover:bg-buttonTxt border 
border-current rounded-xl  transition-all duration-300 font-medium whitespace-nowrap"
                        >
                          Add Repayment
                        </button>
                      )}

                      {!["completed", "rejected"].includes(loan.status) && (
                        <Button variant="secondary" icon={Pencil} onClick={() => navigate(`/loans-given/edit/${loanId}`)}>
                          Edit Loan
                        </Button>
                      )}

                      <button
                        onClick={() => navigate("/loans-given")}
                        className="group flex items-center justify-center gap-2 px-3 sm:px-3 py-2.5 sm:py-2 text-xs sm:text-sm text-buttonBg bg-buttonTxt hover:text-buttonTxt 
hover:bg-buttonBg border border-current rounded-xl  transition-all duration-300 font-medium whitespace-nowrap"
                      >
                        <ArrowLeft className="w-4 h-4" /> Back to Loans
                      </button>

                    </div>
                  </div>
                </div>


                {/* Main Content */}
                <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-6">
                  {/* Header Section */}
                  <div className="bg-primary p-4 sm:p-4 sm:pl-6">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                      <div>
                        <h2 className="text-xl sm:text-lg font-semibold text-secondary mb-2">{loan.loan_id}</h2>
                        <p className="font-semibold text-secondary text-sm sm:text-base">
                          {loan.borrower_name}
                          {loan.borrower_company && ` - ${loan.borrower_company}`} - 
                          <span className='pl-1'>{loan.borrower_type.replace(/_/g, ' ').toUpperCase()} • {loan.loan_type.replace(/_/g, ' ').toUpperCase()}</span>
                        </p>
                      </div>
                      <div className="flex flex-col gap-2 items-start sm:items-end">
                        <span className={`px-4 py-2 rounded-xl text-sm font-semibold ${getStatusBadge(loan.status)}`}>
                          {loan.status.toUpperCase()}
                        </span>
                        {loan.is_overdue && (
                          <span className="px-4 py-2 rounded-full text-sm font-semibold bg-red-100 text-red-700">
                            ⚠️ OVERDUE
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Details Sections */}
                  <div className="p-4 sm:p-8 space-y-6 sm:space-y-8">
                    {/* Loan Financial Summary */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Financial Summary
                      </h3>
                      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
                        <div className="bg-blue-50 p-3 sm:p-4 rounded-xl border border-blue-200">
                          <label className="block text-xs text-gray-600 mb-1">Loan Amount</label>
                          <p className="text-lg sm:text-xl font-bold text-blue-700">{formatCurrency(loan.loan_amount)}</p>
                        </div>
                        <div className="bg-purple-50 p-3 sm:p-4 rounded-xl border border-purple-200">
                          <label className="block text-xs text-gray-600 mb-1">Interest Rate</label>
                          <p className="text-lg sm:text-xl font-bold text-purple-700">{loan.interest_rate}% p.a.</p>
                        </div>
                        <div className="bg-green-50 p-3 sm:p-4 rounded-xl border border-green-200">
                          <label className="block text-xs text-gray-600 mb-1">Total Amount</label>
                          <p className="text-lg sm:text-xl font-bold text-green-700">{formatCurrency(loan.total_amount)}</p>
                        </div>
                        <div className="bg-orange-50 p-3 sm:p-4 rounded-xl border border-orange-200">
                          <label className="block text-xs text-gray-600 mb-1">Amount Paid</label>
                          <p className="text-lg sm:text-xl font-bold text-orange-700">{formatCurrency(loan.amount_paid)}</p>
                        </div>
                        <div className="bg-red-50 p-3 sm:p-4 rounded-xl border border-red-200">
                          <label className="block text-xs text-gray-600 mb-1">Amount Pending</label>
                          <p className="text-lg sm:text-xl font-bold text-red-700">{formatCurrency(loan.amount_pending)}</p>
                        </div>
                        <div className="bg-indigo-50 p-3 sm:p-4 rounded-xl border border-indigo-200">
                          <label className="block text-xs text-gray-600 mb-1">Loan Date</label>
                          <p className="text-base sm:text-lg font-bold text-indigo-700">{formatDateShort(loan.loan_date)}</p>
                        </div>
                        <div className="bg-pink-50 p-3 sm:p-4 rounded-xl border border-pink-200">
                          <label className="block text-xs text-gray-600 mb-1">Due Date</label>
                          <p className="text-base sm:text-lg font-bold text-pink-700">{formatDateShort(loan.due_date)}</p>
                        </div>
                        <div className="bg-teal-50 p-3 sm:p-4 rounded-xl border border-teal-200">
                          <label className="block text-xs text-gray-600 mb-1">Repayment Frequency</label>
                          <p className="text-base sm:text-lg font-bold text-teal-700">{loan.repayment_frequency.replace(/_/g, ' ').toUpperCase()}</p>
                        </div>
                      </div>
                    </div>

                    {/* Borrower Information */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Borrower Information
                      </h3>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Borrower Name</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-words">{loan.borrower_name}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Borrower Type</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-words">{loan.borrower_type.replace(/_/g, ' ').toUpperCase()}</p>
                        </div>
                        {loan.borrower_company && (
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Company Name</label>
                            <p className="text-sm sm:text-base text-secondary font-semibold break-words">{loan.borrower_company}</p>
                          </div>
                        )}
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Contact Number</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-words">{loan.borrower_contact}</p>
                        </div>
                        {loan.borrower_email && (
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Email</label>
                            <p className="text-sm sm:text-base text-secondary font-semibold break-words break-all">{loan.borrower_email}</p>
                          </div>
                        )}
                        {loan.borrower_address && (
                          <div className="sm:col-span-2">
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">{t('common.address')}</label>
                            <p className="text-sm sm:text-base text-secondary font-semibold break-words">{loan.borrower_address}</p>
                          </div>
                        )}
                        {loan.user_details && (
                          <>
                            <div>
                              <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Name / Location</label>
                              <p className="text-sm sm:text-base text-secondary font-semibold break-words">
                                {formatUsernameLocation(
                                  loan.user_details.name || loan.user_details.username,
                                  loan.user_details.location || loan.user_details.city,
                                  loan.user_details.user_id
                                )}
                              </p>
                            </div>
                            <div>
                              <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Role</label>
                              <p className="text-sm sm:text-base text-secondary font-semibold break-words">{loan.user_details.role_name}</p>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Loan Purpose & Details */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Loan Purpose & Details
                      </h3>
                      <div className="grid grid-cols-2 sm:grid-cols-2 gap-2 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Purpose</label>
                          <p className="text-sm sm:text-base text-gray-900 bg-gray-50 p-3 sm:p-4 rounded-lg">{loan.purpose}</p>
                        </div>
                        {loan.collateral_details && (
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Collateral Details</label>
                            <p className="text-sm sm:text-base text-gray-900 bg-gray-50 p-3 sm:p-4 rounded-lg">{loan.collateral_details}</p>
                          </div>
                        )}
                        {loan.remarks && (
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Remarks</label>
                            <p className="text-sm sm:text-base text-gray-900 bg-gray-50 p-3 sm:p-4 rounded-lg">{loan.remarks}</p>
                          </div>
                        )}
                        {(loan.has_agreement || loan.agreement_url) && (
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-2">Agreement Document</label>
                            <button
                              onClick={viewAgreementDocument}
                              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium flex items-center gap-2"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                              View Agreement Document
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Approval Information */}
                    {loan.approved_by && (
                      <div>
                        <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                          Approval Information
                        </h3>
                        <div className="grid grid-cols-2 sm:grid-cols-2 gap-2 sm:gap-6">
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Approved By</label>
                            <p className="text-sm sm:text-base text-secondary font-semibold break-words">{loan.approved_by}</p>
                          </div>
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Approved Date</label>
                            <p className="text-sm sm:text-base text-secondary font-semibold break-words">{formatDate(loan.approved_date)}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Rejection Information */}
                    {loan.status === 'rejected' && loan.rejection_reason && (
                      <div>
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 pb-2 border-b-2 border-red-500">
                          Rejection Information
                        </h3>
                        <div className="bg-red-50 p-3 sm:p-4 rounded-lg border border-red-200">
                          <label className="block text-xs sm:text-sm font-medium text-red-700 mb-1">Rejection Reason</label>
                          <p className="text-sm sm:text-base text-red-900">{loan.rejection_reason}</p>
                        </div>
                      </div>
                    )}

                    {/* System Information */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        System Information
                      </h3>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">{t('common.createdBy')}</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-words">{loan.created_by}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Created At</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-words">{formatDate(loan.created_at)}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Last Updated</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-words">{formatDate(loan.updated_at)}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Repayment History */}
                {loan.repayments && loan.repayments.length > 0 && (
                  <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                    <div className="bg-gradient-to-r from-green-500 to-green-600 p-4 sm:p-6">
                      <h3 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
                        💰 Repayment History ({loan.repayments_count})
                      </h3>
                      <p className="text-green-100 text-sm mt-1">
                        Total Repaid: {formatCurrency(loan.amount_paid)}
                      </p>
                    </div>

                    <div className="p-4 sm:p-6">
                      {/* Desktop Table */}
                      <div className="hidden lg:block overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b-2 border-gray-200">
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Date</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Amount</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Payment Mode</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Transaction ID</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Received By</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {displayedRepayments.map((repayment) => (
                              <tr key={repayment.repayment_id} className="hover:bg-gray-50">
                                <td className="px-4 py-3 text-sm">{formatDateShort(repayment.payment_date)}</td>
                                <td className="px-4 py-3 text-sm font-bold text-green-600">{formatCurrency(repayment.amount)}</td>
                                <td className="px-4 py-3 text-sm">{repayment.payment_mode.replace(/_/g, ' ').toUpperCase()}</td>
                                <td className="px-4 py-3 text-sm">{repayment.transaction_id || 'N/A'}</td>
                                <td className="px-4 py-3 text-sm">{repayment.received_by}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Mobile Cards */}
                      <div className="lg:hidden space-y-3">
                        {displayedRepayments.map((repayment) => (
                          <div key={repayment.repayment_id} className="border border-gray-200 rounded-lg p-4">
                            <div className="flex justify-between items-start mb-3">
                              <div>
                                <p className="font-semibold text-green-600">{formatCurrency(repayment.amount)}</p>
                                <p className="text-sm text-gray-600">{formatDateShort(repayment.payment_date)}</p>
                              </div>
                              <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                                {repayment.payment_mode.replace(/_/g, ' ').toUpperCase()}
                              </span>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm">
                              <div>
                                <span className="text-gray-600">Transaction ID:</span>
                                <p className="font-medium">{repayment.transaction_id || 'N/A'}</p>
                              </div>
                              <div>
                                <span className="text-gray-600">Received By:</span>
                                <p className="font-medium">{repayment.received_by}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Show More/Less Button */}
                      {loan.repayments.length > 5 && (
                        <div className="mt-4 text-center">
                          <Button variant="pagination" onClick={() => setShowAllRepayments(!showAllRepayments)}>
                            {showAllRepayments ? '↑ Show Less' : `↓ Show All (${loan.repayments.length - 5} more)`}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
    </DashboardLayout>
  );
};

export default ViewLoanGiven;